<script setup>
import {VIEWBOX_SIZE} from './composables/radialChart';
import InnerTrack from './partials/InnerTrack.vue';
import GraphicalObjects from "./partials/GraphicalObjects.vue";
import RadialSegments from "./partials/RadialSegments.vue";
import ChartCentre from "./partials/ChartCentre.vue";
import IndustryAverageLegend from "@/components/seo-score/visibility-chart/partials/IndustryAverageLegend.vue";
import {CENTRE_FADE_DURATION_MS, useRadialAnimation} from './composables/animation';
import {computed} from "vue";
import LLMLogos from "@/components/seo-score/visibility-chart/partials/LLMLogos.vue";
import ChartCentreLoading from "@/components/seo-score/visibility-chart/partials/ChartCentreLoading.vue";

const props = defineProps({
  /**
   * Value to display, from 0 to 100.
   * Can be null/undefined while data is loading; in that case
   * no radial animation should run.
   */
  value: {
    type: [Number, null],
    required: false,
    default: null,
  },
  /**
   * Descriptive label shown under the main value.
   */
  label: {
    type: String,
    default: 'Visibility Score',
  },

  /**
   * Expects float between -1 and 1.
   * Percentage difference value,used to render the small pill underneath the label, like "↑ 8%".
   */
  percentageChange: {
    type: [Number, null],
    default: null,
  },
  /**
   * Value to display as the industry average along the inner track
   */
  industryAverage: {
    type: [Number, null],
    required: false,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const displayValue = computed(() => props.value !== null ? parseFloat((props.value * 100).toFixed(2)) : null);

const {currentValue, startPostAnimationEffects} = useRadialAnimation(
  () => displayValue.value,
  () => props.loading,
);
</script>

<template>
  <svg
    width="100%"
    height="100%"
    :viewBox="`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE * 0.75}`"
    xmlns="http://www.w3.org/2000/svg"
  >
    <GraphicalObjects />

    <IndustryAverageLegend
      :industry-average="industryAverage"
      :start-post-animation-effects="startPostAnimationEffects"
      :style="{
        opacity: !loading ? 1 : 0,
        transition: `opacity ${CENTRE_FADE_DURATION_MS}ms ease`,
      }"
    />

    <InnerTrack
      :industry-average="industryAverage"
      :start-post-animation-effects="startPostAnimationEffects"
    />

    <RadialSegments :value="currentValue" />

    <ChartCentreLoading :loading="loading" />

    <ChartCentre
      :current-value="currentValue"
      :final-value="displayValue"
      :label="label"
      :percentage-change="percentageChange"
      :style="{
        opacity: !loading ? 1 : 0,
        transition: `opacity ${CENTRE_FADE_DURATION_MS}ms ease`,
      }"
    />

    <g :transform="`translate(${VIEWBOX_SIZE * 0.29}, ${VIEWBOX_SIZE * 0.68}) scale(${VIEWBOX_SIZE * 0.002})`">
      <LLMLogos />
      <rect
        v-tooltip="{
          content: 'This score tracks your visibility across all the places customers search, including Google, Maps, and AI tools.',
          placement: 'bottom',
          theme: 'dark-blue-tooltip',
        }"
        width="216"
        height="30"
        fill="transparent"
        pointer-events="all"
      />
    </g>
  </svg>
</template>

